import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import {
	Grid,
	Typography,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableCell,
	TableBody,
	Tooltip,
	Select,
	MenuItem,
	Button,
	CircularProgress
} from "@material-ui/core"
import { KeyboardTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers"

import {
	CheckCircle,
	Block,
	WarningTwoTone as WarningIcon
} from "@material-ui/icons"

import { ptBR } from "date-fns/locale"
import DateFnsUtils from "@date-io/date-fns"
import clsx from "clsx"

import {
	transformHourAndMinutesToDate,
	transformDateIntoHourAndMinutes,
	isHourInRange,
	getTotalMinutes
} from "@/utils/time"

import {
	ActionDialog,
	Divider,
	Loading,
	Notification,
	Portlet
} from "@/components"
import AlertContainer from "@/components/AlertContainer"
import ChannelGroupPermissionGate from "@/components/PermissionGate/ChannelGroupPermissionGate"

import useDidMount from "@/hooks/useDidMount"

import InboxChannelSettings, {
	BaseInboxChannelSettings,
	SendMessagesTimeLimitsValueType
} from "@/services/InboxChannelSettings"

import useStyles from "@/pages/Admin/Settings/InboxChannel/SendMessagesTimeLimits/styles"

export type UseParamsType = {
	inboxChannelId: string
}

type SendMessagesTimeLimitsWarnings = {
	[key: number]: Array<"all_day" | "from_hour" | "to_hour">
}

type OptionType = "no_send" | "all_day" | "customize";

type IconType = "no_send" | "warning" | "enabled"

const RECOMMENDED_START_TIME = {
	hours: 8,
	minutes: 0
}

const RECOMMENDED_END_TIME = {
	hours: 20,
	minutes: 0
}

const RECOMMENDATION_MESSAGE = "Para seguir as boas práticas de envio Letalk, sugerimos enviar mensagem entre às 08:00 e 20:00! Enviar mensagem fora deste horário aumenta o risco de banimento."

const SendMessagesTimeLimits = () => {
	const params = useParams<UseParamsType>()

	const [sendMessagesTimeLimits, setSendMessagesTimeLimits] = useState<BaseInboxChannelSettings<"send_messages_time_limits">>()
	const [loading, setLoading] = useState<boolean>(false)
	const [sendMessagesTimeLimitsHasChanged, setSendMessagesTimeLimitsHasChanged] = useState<boolean>(false)
	const [sendMessagesTimeLimitsUpdating, setSendMessagesTimeLimitsUpdating] = useState<boolean>(false)
	const [hasErrors, setHasErrors] = useState<boolean>(false)
	const [hasWarnings, setHasWarnings] = useState<boolean>(false)
	const [endDateIsLessOrEqualThanStartDate, setEndIsLessOrEqualThanStart] = useState<boolean>(false)
	const [inputTimeWarnings, setInputTimeWarnings] = useState<SendMessagesTimeLimitsWarnings>({
		1: [],
		2: [],
		3: [],
		4: [],
		5: [],
		6: [],
		7: []
	})

	const classes = useStyles()

	const parseHourAndMinutes = (hourAndMinutes: string) => {
		const [hour, minutes] = hourAndMinutes?.split("\n")?.[0]?.split(":")

		return [Number(hour), Number(minutes)]
	}

	const validateWarningsInSendMessageLimitsTime = (limits: SendMessagesTimeLimitsValueType[]) => {
		const inputWarnings: SendMessagesTimeLimitsWarnings = {}

		limits.forEach(limit => {
			if (!inputWarnings[limit.day_code]) {
				inputWarnings[limit.day_code] = []
			}

			const [startHour, startMinute] = parseHourAndMinutes(limit.from_hour)
			const [endHour, endMinute] = parseHourAndMinutes(limit.to_hour)

			const startTotalMinutes = getTotalMinutes(startHour || 0, startMinute || 0)
			const endTotalMinutes = getTotalMinutes(endHour || 0, endMinute || 0)

			if (endTotalMinutes <= startTotalMinutes) {
				Notification.error({ message: "O horário de término deve ser maior que o horário de início." })
				inputWarnings[limit.day_code].push("from_hour")
				inputWarnings[limit.day_code].push("to_hour")
				setEndIsLessOrEqualThanStart(true)
			}

			if (limit.option === "all_day") {
				inputWarnings[limit.day_code].push("all_day")
			} else {
				const toHourDate = transformHourAndMinutesToDate(limit.to_hour)
				const fromHourDate = transformHourAndMinutesToDate(limit.from_hour)

				if (toHourDate && !isHourInRange(toHourDate, RECOMMENDED_START_TIME, RECOMMENDED_END_TIME)) {
					inputWarnings[limit.day_code].push("to_hour")
				}

				if (fromHourDate && !isHourInRange(fromHourDate, RECOMMENDED_START_TIME, RECOMMENDED_END_TIME)) {
					inputWarnings[limit.day_code].push("from_hour")
				}
			}
		})

		const hasInputWarnings = Object.values(inputWarnings).some(warnings => warnings.length > 0)

		setHasWarnings(hasInputWarnings)
		setInputTimeWarnings(inputWarnings)
	}

	const handleGetSendMessagesTimeLimitSettings = async () => {
		setLoading(true)
		const data = await InboxChannelSettings.get("send_messages_time_limits", params.inboxChannelId)
		setSendMessagesTimeLimits(data)
		setLoading(false)
	}

	useDidMount(() => {
		handleGetSendMessagesTimeLimitSettings()
	})

	const hasInputWarnings = (inputWarnings: SendMessagesTimeLimitsWarnings): {
		inputOptionHasWarning: boolean
		fromOurInputHasWarning: boolean
		toOurInputHasWarning: boolean
		someInputHasWarning: boolean
	} => {
		const values = Object.values(inputWarnings ?? {})

		const inputOptionHasWarningTemp = values.some(warnings => warnings.includes("all_day"))
		const fromOurInputHasWarningTemp = values.some(warnings => warnings.includes("from_hour"))
		const toOurInputHasWarningTemp = values.some(warnings => warnings.includes("to_hour"))

		return {
			inputOptionHasWarning: inputOptionHasWarningTemp,
			toOurInputHasWarning: toOurInputHasWarningTemp,
			fromOurInputHasWarning: fromOurInputHasWarningTemp,
			someInputHasWarning: inputOptionHasWarningTemp || toOurInputHasWarningTemp || fromOurInputHasWarningTemp
		}
	}

	const handleUpdateSendMessagesTimeLimitsValue = async (fieldName: string, newValue: string, index: number) => {
		if (sendMessagesTimeLimits?.value) {
			const allValues = sendMessagesTimeLimits.value

			const oldValue = allValues?.[index]

			allValues[index] = { ...oldValue, [fieldName]: newValue }

			setSendMessagesTimeLimitsHasChanged(true)

			setSendMessagesTimeLimits({ ...sendMessagesTimeLimits, value: [...allValues] })
		}
	}

	const updateSendMessagesTimeLimitSettings = async () => {
		setSendMessagesTimeLimitsUpdating(true)
		if (!hasErrors) {
			InboxChannelSettings.update(sendMessagesTimeLimits?.value, params.inboxChannelId, sendMessagesTimeLimits?.id, sendMessagesTimeLimits?.key)
			setSendMessagesTimeLimitsHasChanged(false)
			Notification.success({ message: "Os horários dos envios foram atualizados." })
		} else {
			Notification.error({ message: "Corrija os dados antes de enviar." })
		}
		setSendMessagesTimeLimitsUpdating(false)
	}

	const handleSubmitSendMessagesTimeLimitSettings = async () => {
		if (hasWarnings) {
			ActionDialog.open({
				title: "ATENÇÃO",
				cancelText: "Não, voltar",
				saveText: "Sim, continuar",
				openDialog: true,
				forcedRender: true,
				onSave: updateSendMessagesTimeLimitSettings,
				children: (
					<Typography
						className={classes.warningDialogText}
					>
						{endDateIsLessOrEqualThanStartDate ? (
							<>
								Um ou mais horários de término estão configurados como iguais ou anteriores ao horário de início. Isso pode gerar problemas em agendamentos de mensagens, como no Soneca com disparo de mensagem e no Envio em massa.<br/><br/>
								<strong>Deseja prosseguir com estes horários?</strong>
							</>
						) : (
							<>
								Percebemos que você incluiu horários fora do horário sugerido. Isso pode aumentar o risco de banimento de chip.<br/><br/>
								<strong>Deseja prosseguir com estes horários?</strong>
							</>
						)}
					</Typography>
				)
			})
		} else {
			await updateSendMessagesTimeLimitSettings()
		}
	}

	const translatedDays: Record<number, string> = {
		1: "Segunda-Feira",
		2: "Terça-Feira",
		3: "Quarta-Feira",
		4: "Quinta-Feira",
		5: "Sexta-Feira",
		6: "Sábado",
		7: "Domingo"
	}

	const handleDateChange = (newValue: Date | null, fieldName: string, index: number) => {
		if (!isNaN(Date.parse(`${newValue}`))) {
			const hourAndMinutes = transformDateIntoHourAndMinutes(newValue as Date)

			handleUpdateSendMessagesTimeLimitsValue(fieldName, hourAndMinutes, index)
		}
	}

	const iconTypeToIconConfig: Record<IconType, { component: React.ReactNode, className: string}> = {
		no_send: {
			component: <Block className={classes.disableIcon} />,
			className: classes.disableIcon
		},
		warning: {
			component: (
				<Tooltip title={RECOMMENDATION_MESSAGE}>
					<WarningIcon className={classes.warningIcon} />
				</Tooltip>
			),
			className: classes.warningIcon
		},
		enabled: {
			component: <CheckCircle className={classes.enableIcon} />,
			className: classes.enableIcon
		}
	}
	const getOptionStatusIcon = (option: OptionType, hasWarning: boolean) => {
		let iconType: IconType

		if (option === "no_send") {
			iconType = "no_send"
		} else if (hasWarning) {
			iconType = "warning"
		} else {
			iconType = "enabled"
		}

		return iconTypeToIconConfig[iconType].component
	}

	useEffect(() => {
		if (sendMessagesTimeLimits?.value) {
			validateWarningsInSendMessageLimitsTime(sendMessagesTimeLimits.value)
		}
	}, [sendMessagesTimeLimits?.value])

	const isSubmitButtonDisabled = sendMessagesTimeLimitsUpdating || !sendMessagesTimeLimitsHasChanged

	return (
		<Loading
			loading={loading}
		>
			<Portlet>
				<AlertContainer
					title="RECOMENDAÇÕES"
					alertType="info"
				>
					<Typography
						style={{
							fontSize: "14px",
							fontWeight: 400,
							lineHeight: "20px"
						}}
					>
						{RECOMMENDATION_MESSAGE}
					</Typography>
				</AlertContainer>

				<Divider orientation="horizontal" size={2} />

				<TableContainer>
					<Table
						stickyHeader={false}
						size="small"
						style={{ borderRadius: "8px" }}
					>
						<TableHead>
							<TableRow>
								<TableCell width={150}>
									DIA
								</TableCell>

								<TableCell>
									OPÇÕES DE HORÁRIOS
								</TableCell>

								<TableCell>
									INÍCIO
								</TableCell>

								<TableCell>
									TÉRMINO
								</TableCell>

								<TableCell> </TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{sendMessagesTimeLimits?.value?.map((limit, index) => {
								const {
									fromOurInputHasWarning,
									toOurInputHasWarning,
									someInputHasWarning,
									inputOptionHasWarning
								} = hasInputWarnings(inputTimeWarnings[limit.day_code] as unknown as SendMessagesTimeLimitsWarnings)

								return (
									<TableRow
										key={limit.day_code}
										tabIndex={-1}
									>
										<TableCell
										>
											<Typography variant="body1">
												{translatedDays[limit.day_code]}
											</Typography>
										</TableCell>

										<TableCell>
											<ChannelGroupPermissionGate action="edit">
												<Select
													fullWidth
													value={limit.option}
													variant="outlined"
													onChange={(event) => {
														if (event.target.value) {
															handleUpdateSendMessagesTimeLimitsValue("option", String(event.target.value), index)
														}
													}}
													className={clsx(classes.selectInput, inputOptionHasWarning && classes.warning)}
												>
													<MenuItem value="all_day">O dia todo</MenuItem>
													<MenuItem value="customize">Personalizado</MenuItem>
													<MenuItem value="no_send">Não enviar</MenuItem>
												</Select>
											</ChannelGroupPermissionGate>
										</TableCell>

										<TableCell>
											<MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
												<Grid>
													<ChannelGroupPermissionGate action="edit">
														<KeyboardTimePicker
															okLabel="Pronto"
															clearLabel="Limpar"
															cancelLabel="Cancelar"
															disabled={limit.option === "no_send" || limit.option === "all_day"}
															id="from-hour-time-picker"
															ampm={false}
															value={transformHourAndMinutesToDate(limit.from_hour)}
															invalidDateMessage="Valores Incorretos"
															inputVariant="outlined"
															size="small"
															onError={(_error) => {
																if (_error) {
																	setHasErrors(true)
																}
															}}
															onChange={(newValue) => {
																handleDateChange(newValue, "from_hour", index)
																setHasErrors(false)
															}}
															KeyboardButtonProps={{
																"aria-label": "change time"
															}}
															className={fromOurInputHasWarning ? classes.warning : ""}
														/>
													</ChannelGroupPermissionGate>
												</Grid>
											</MuiPickersUtilsProvider>
										</TableCell>

										<TableCell>
											<MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
												<Grid>
													<ChannelGroupPermissionGate action="edit">
														<KeyboardTimePicker
															okLabel="Pronto"
															clearLabel="Limpar"
															cancelLabel="Cancelar"
															disabled={limit.option === "no_send" || limit.option === "all_day"}
															id="to-hour-time-picker"
															ampm={false}
															value={transformHourAndMinutesToDate(limit.to_hour)}
															inputVariant="outlined"
															invalidDateMessage="Valores Incorretos"
															size="small"
															onError={(_error) => {
																if (_error) {
																	setHasErrors(true)
																}
															}}
															onChange={(newValue) => {
																handleDateChange(newValue, "to_hour", index)
																setHasErrors(false)
															}}
															KeyboardButtonProps={{
																"aria-label": "change time"
															}}
															className={toOurInputHasWarning ? classes.warning : ""}
														/>
													</ChannelGroupPermissionGate>
												</Grid>
											</MuiPickersUtilsProvider>
										</TableCell>

										<TableCell
											className={classes.statusIconCell}
										>
											{getOptionStatusIcon(limit.option, someInputHasWarning)}
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>

					</Table>
				</TableContainer>

				<Divider orientation="horizontal" size={2} />

				<Grid container justify="flex-end">
					<Grid item>
						<ChannelGroupPermissionGate action="edit">
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmitSendMessagesTimeLimitSettings}
								disabled={isSubmitButtonDisabled}
							>
								{sendMessagesTimeLimitsUpdating ? <CircularProgress size={15} /> : "Salvar"}
							</Button>
						</ChannelGroupPermissionGate>
					</Grid>
				</Grid>
			</Portlet>
		</Loading>

	)
}

export default SendMessagesTimeLimits
